/**
 * A deadline for a read that would otherwise hang forever.
 *
 * Firestore does not give up on a stalled connection — it waits for one to
 * come back. On a desk that is the right behaviour. On a phone that has
 * wandered between a building's wifi and cellular it produces the thing Matt
 * reported on 2026-10-19: a Receipts screen reading "Loading…" indefinitely,
 * which only cleared when he left the screen and came back, because that
 * started a fresh request.
 *
 * A spinner with no end is the worst of the three possible states — worse than
 * an error, which at least offers a retry, and worse than stale data, which at
 * least shows something. This converts it into the first.
 *
 * The timeout is generous on purpose. It is not here to make a slow network
 * fail faster; it is here to catch a request that is never going to finish.
 */

export const READ_DEADLINE_MS = 20_000

export class ReadTimedOut extends Error {
  /** Matches Firestore's own code for the same condition, so one branch handles both. */
  readonly code = 'deadline-exceeded'

  constructor() {
    super('That took too long to load. Check your connection and try again.')
    this.name = 'ReadTimedOut'
  }
}

/**
 * Reject if `work` has not settled within the deadline.
 *
 * The underlying request is *not* cancelled — Firestore's SDK owns it, and
 * killing it is not ours to do. If it completes later its result is simply
 * dropped, and the retry that follows is served from the SDK's own cache of
 * the connection it has by then re-established.
 */
export async function withDeadline<T>(
  work: Promise<T>,
  ms: number = READ_DEADLINE_MS,
): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      work,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new ReadTimedOut()), ms)
      }),
    ])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}
